import { ReactNode, useEffect, useMemo } from "react";
import { useSiteTheme, getThemeStyles } from "@/hooks/useSiteTheme";
import { useSiteContent } from "@/hooks/useSiteContent";

/** Convert hex (#rrggbb) to HSL string "h s% l%" for CSS variable usage */
const hexToHsl = (hex: string): string | null => {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return null;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
};

/**
 * Applies active theme CSS variables to the document root.
 * Also applies custom color overrides from site_settings.
 */
const StorefrontTheme = ({ children }: { children: ReactNode }) => {
  const { theme } = useSiteTheme();
  const { content } = useSiteContent();
  const styles = useMemo(() => getThemeStyles(theme), [theme]);

  // Build custom color overrides from site_settings
  const customOverrides = useMemo(() => {
    const overrides: Record<string, string> = {};
    const colorMap: [string, string, string[]][] = [
      ["custom_color_primary", "", ["--primary", "--accent", "--ring"]],
      ["custom_color_background", "", ["--background"]],
      ["custom_color_foreground", "", ["--foreground", "--card-foreground", "--popover-foreground", "--secondary-foreground"]],
      ["custom_color_secondary", "", ["--secondary", "--muted"]],
      ["custom_color_accent", "", ["--accent"]],
      ["custom_color_card", "", ["--card", "--popover"]],
      ["custom_color_border", "", ["--border", "--input"]],
      ["custom_color_muted", "", ["--muted"]],
    ];

    for (const [key, , cssVars] of colorMap) {
      const hex = content[key]?.trim();
      if (!hex) continue;
      const hsl = hexToHsl(hex);
      if (!hsl) continue;
      for (const v of cssVars) {
        overrides[v] = hsl;
      }
    }
    return overrides;
  }, [
    content.custom_color_primary,
    content.custom_color_background,
    content.custom_color_foreground,
    content.custom_color_secondary,
    content.custom_color_accent,
    content.custom_color_card,
    content.custom_color_border,
    content.custom_color_muted,
  ]);

  useEffect(() => {
    const root = document.documentElement;
    const entries = Object.entries(styles);
    entries.forEach(([key, value]) => root.style.setProperty(key, value));

    // Apply custom color overrides on top
    const overrideEntries = Object.entries(customOverrides);
    overrideEntries.forEach(([key, value]) => root.style.setProperty(key, value));

    return () => {
      entries.forEach(([key]) => root.style.removeProperty(key));
      overrideEntries.forEach(([key]) => root.style.removeProperty(key));
    };
  }, [styles, customOverrides]);

  // Merge styles + overrides for inline style on wrapper
  const mergedStyles = useMemo(() => {
    return { ...styles, ...customOverrides };
  }, [styles, customOverrides]);

  return (
    <div style={mergedStyles as any} className="storefront-themed">
      {children}
    </div>
  );
};

export default StorefrontTheme;
